
import React, { ChangeEvent, useState, useEffect, useRef } from "react";
import { Input, Button, Popup, InputOnChangeData, Label, Ref } from "semantic-ui-react";
import { useTranslation } from "react-i18next";

import { HourMinuteSelect } from "./HourMinuteSelect/HourMinuteSelect";
import Validate, { ERangeError, getTimeRangeErrorMessage } from "./DateTimePicker.validation"
import { useOnClickOutside } from "../../../../../sg-hooks/useOnClickOutside";


interface ITimeInput {
    value?: string;
    minDate?: Date;
    maxDate?: Date;
    errorMessage?: string;
    onChange: (date: string | undefined) => void;
}


export const TimeInput: React.FC<ITimeInput> = ({
    value,
    onChange,
    minDate,
    maxDate,
    errorMessage,
}) => {
    const { t } = useTranslation()

    const inputRef = useRef<HTMLInputElement>(null);
    const inputElement = inputRef.current?.getElementsByTagName("INPUT")[0] as HTMLInputElement | undefined;

    const [inputError, setInputError] = useState<string | undefined>();
    const [isOpen, setOpen] = useState(false);


    const update = (inputValue?: string, strict = false) => {
        if (!inputValue) {
            return onChange(inputValue)
        };

        inputValue = inputValue.replace(".", ":");

        const inputTextSeemsCompleteDate = Validate.validateTimeInput(inputValue, strict)

        if (!inputTextSeemsCompleteDate) {
            return
        };

        const rangeError = Validate.validateTimeRange(inputValue, { minDate, maxDate });
        if (rangeError) {
            const errorMessage = getTimeRangeErrorMessage(rangeError, { minDate, maxDate }, t);

            setInputError(errorMessage);
            if (rangeError === ERangeError.InvalidDate) {
                onChange(undefined);
            }
            onChange(inputValue);
            return
        }

        onChange(inputValue)
    };


    useEffect(() => {
        const inputElement = inputRef.current?.getElementsByTagName("INPUT")[0] as HTMLInputElement | undefined;
        if (inputElement && typeof value !== undefined && (inputElement.value || "") !== value) {
            inputElement.value = value || "";
        }
    }, [value]);


    const _onInputChange = (event: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        inputError && setInputError(undefined);
        update(event.target.value, true);
    };

    const _onOutsideClick = () => {
        if (inputElement && inputElement === document.activeElement) {
            const value = inputElement?.value
            inputError && setInputError(undefined);
            update(value);
        }
    };

    const _onTimeChange = (value?: string) => {
        if (inputElement) {
            inputElement.value = value || ""
        }
        update(value)
    }

    useOnClickOutside(inputRef, _onOutsideClick);
    return (
        <div style={{ position: "relative", width: "100%" }}>
            <Popup
                on="click"

                open={isOpen}
                position="bottom right"
                style={{ padding: 0, zIndex: 9999999 }}
                onClose={() => setOpen(false)}
                trigger={(
                    <Ref innerRef={inputRef}>
                        <Input
                            placeholder="hh:mm"
                            onChange={_onInputChange}
                            error={Boolean(inputError)}
                            label={
                                <Button
                                    type="button"
                                    icon="clock"
                                    onClick={() => setOpen((open) => !open)}
                                />
                            }
                            labelPosition="right"
                        />
                    </Ref>
                )}
            >
                <HourMinuteSelect
                    value={value}
                    maxDate={maxDate}
                    minDate={minDate}
                    onChange={_onTimeChange}
                />
            </Popup>


            {(inputError || errorMessage) && <Label pointing negative prompt> {(inputError || errorMessage)}</Label>}
        </div>
    )
};
